/**
 * Handles the cases where we require to manage the cash float
 * @todo This is a bare minimum approach which caters for the
 * `open-register-panel` case to make sure this form works.
 * This will evolve as we move further into the cycle/migration.
 */
import {
  FieldLabel,
  InputCharLimit,
  Stack,
  TextAreaInput,
} from '@lightspeed/design-system-react'
import React, { FormEvent, useEffect, useMemo, useState } from 'react'
import { useT } from '@transifex/react'
import { CurrencyInputWithSymbol } from '@vend/business-components'
import { formatNumber, toFixedDecimalPlaces } from '@vend/utilities'
import { detectCreditCard } from '@vend/credit-card-detector'
import DOMPurify from 'dompurify'
import { CASH_MOVEMENT_TYPES } from '../../constants'
import { useRetailerSettingsManager } from '../../ng-compat/hooks'
import { HasCCErrorMessage } from '../shared/has-cc-error-message/HasCCErrorMessage'
import { CashFloatSettings } from './utils/types'

const MAXIMUM_NOTE_CHARACTERS = 255
const THRESHOLD_NOTE_CHARACTERS = 50
interface Props {
  onSubmit: (settings: CashFloatSettings) => void
  onError: (hasErrors: boolean) => void
  type?: CASH_MOVEMENT_TYPES
  noteRows?: number
  className?: string
  children?: React.ReactNode
}

export const CashFloatForm: React.FC<Props> = ({
  onSubmit,
  onError,
  type = CASH_MOVEMENT_TYPES.OPENING_CASH_FLOAT,
  noteRows,
  className,
  children,
}) => {
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [hasCreditCardNumber, setHasCCNumber] = useState(false)
  const [hasAmountError, setHasAmountError] = useState(false)
  const [hasNoteError, setHasNoteError] = useState(false)

  const t = useT()
  const retailer = useRetailerSettingsManager().get()
  useEffect(() => {
    if (hasAmountError || hasNoteError) {
      onError(true)
    } else {
      onError(false)
    }
  }, [hasAmountError, hasNoteError, onError])

  const handleAmountBlur = (number: number | string) => {
    const roundedValue = toFixedDecimalPlaces(number, 2)
    setAmount(roundedValue)
  }
  const handleNoteChange = (value: string) => {
    const hasCCError = detectCreditCard(value)
    setHasCCNumber(hasCCError)

    const sanitizedValue = DOMPurify.sanitize(value)
    setHasNoteError(
      sanitizedValue.length > MAXIMUM_NOTE_CHARACTERS || hasCCError
    )
    setNote(sanitizedValue)
  }
  const handleAmountChange = (amount: number) => {
    setAmount(amount.toString())
    setHasAmountError(false)
  }
  const onSubmitHandler = (event: FormEvent) => {
    event.preventDefault()
    onSubmit({
      amount: toFixedDecimalPlaces(amount, 2),
      note: note.trim(),
      type,
    })
  }

  const getAmountInputLabel = (type: CASH_MOVEMENT_TYPES) => {
    if (type === CASH_MOVEMENT_TYPES.OPENING_CASH_FLOAT) {
      return t('Opening float', { _context: 'Form label' })
    }
    return t('Amount', { _context: 'Form label' })
  }

  const placeholder = useMemo(
    () => formatNumber(0, retailer.culture, { minimumFractionDigits: 2 }),
    [retailer.culture]
  )

  return (
    <form onSubmit={onSubmitHandler} className={className}>
      <Stack space={5}>
        <FieldLabel label={getAmountInputLabel(type)}>
          <CurrencyInputWithSymbol
            value={amount || ''}
            onBlur={number => handleAmountBlur(number)}
            onChange={handleAmountChange}
            placeholder={placeholder}
            validators={{
              nonNegative: true,
            }}
            onError={() => setHasAmountError(true)}
          />
        </FieldLabel>
        <FieldLabel
          label={t('Note', { _context: 'Form label' })}
          supplementaryLabel={t('Optional', {
            _context: 'Form supplementary field',
          })}
        >
          <TextAreaInput
            rows={noteRows}
            onChange={event => handleNoteChange(event.target.value)}
            placeholder={t('Add a note', {
              _context: 'Placeholder text for a form field',
            })}
            value={note}
            hasError={
              note.length > MAXIMUM_NOTE_CHARACTERS || hasCreditCardNumber
            }
          ></TextAreaInput>
          {note.length > MAXIMUM_NOTE_CHARACTERS - THRESHOLD_NOTE_CHARACTERS ? (
            <InputCharLimit
              limit={MAXIMUM_NOTE_CHARACTERS}
              value={note}
              threshold={THRESHOLD_NOTE_CHARACTERS}
            />
          ) : null}
          {hasCreditCardNumber && <HasCCErrorMessage />}
        </FieldLabel>
        {children}
      </Stack>
    </form>
  )
}
